import type { Ref } from 'vue';
import type { EventDetailDTO } from '~/shared/api/client/events/events.dto';
import {
  OG_SITE_NAME,
  getAbsoluteUrl,
  getOgImageMeta,
  stripHtml,
} from '~/shared/lib/seo';
import { useOrigin } from '~/shared/lib/useOrigin';
import { getEventSeoDates } from '~/shared/lib/getEventSeoDates';
import OgImage from '~/shared/images/og-image.png';

const OG_TYPE = 'event';
const FALLBACK_TITLE = 'CRE Events';

function getEventStatus(status: string) {
  const normalizedStatus = status.toLowerCase();

  if (normalizedStatus.includes('cancel')) {
    return 'https://schema.org/EventCancelled';
  }

  if (normalizedStatus.includes('postpon')) {
    return 'https://schema.org/EventPostponed';
  }

  return 'https://schema.org/EventScheduled';
}

export function useEventDetailSeo(
  event: Ref<EventDetailDTO | null | undefined>,
) {
  const route = useRoute();
  const origin = useOrigin();

  useHead(() => {
    const eventData = event.value;
    const url = `${origin}${route.fullPath}`;
    const title = eventData?.name || FALLBACK_TITLE;
    const description = stripHtml(eventData?.description) || title;
    const image =
      getAbsoluteUrl(origin, eventData?.image?.photo?.file_url) ||
      getAbsoluteUrl(origin, OgImage);
    const { startDate, endDate } = eventData
      ? getEventSeoDates(eventData)
      : { startDate: undefined, endDate: undefined };

    const jsonLdGraph = eventData
      ? [
          {
            '@type': 'Event',
            name: eventData.name,
            description,
            image: [image],
            startDate,
            endDate,
            eventStatus: getEventStatus(eventData.status),
            eventAttendanceMode:
              'https://schema.org/OfflineEventAttendanceMode',
            url,
            location: eventData.place
              ? {
                  '@type': 'Place',
                  name: eventData.place,
                  address: eventData.place,
                }
              : undefined,
            organizer: {
              '@type': 'Organization',
              name: OG_SITE_NAME,
              url: origin,
            },
            offers: eventData.url
              ? {
                  '@type': 'Offer',
                  url: eventData.url,
                  availability: 'https://schema.org/InStock',
                }
              : undefined,
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Главная',
                item: origin,
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Мероприятия',
                item: `${origin}/events`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: eventData.name,
                item: url,
              },
            ],
          },
        ]
      : [];

    return {
      title,
      meta: [
        { name: 'description', content: description },
        { property: 'og:type', content: OG_TYPE },
        { property: 'og:url', content: url },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:site_name', content: OG_SITE_NAME },
        ...getOgImageMeta(origin, eventData?.image?.photo?.file_url),
        ...(eventData
          ? [
              { property: 'event:start_time', content: startDate ?? '' },
              { property: 'event:end_time', content: endDate ?? '' },
            ]
          : []),
        ...(eventData?.place
          ? [{ property: 'event:location', content: eventData.place }]
          : []),
      ],
      script: eventData
        ? [
            {
              type: 'application/ld+json',
              key: 'ld-json-event',
              innerHTML: {
                '@context': 'https://schema.org',
                '@graph': jsonLdGraph,
              },
            },
          ]
        : [],
    };
  });
}
